import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Activity,
  ArrowRight,
  BookOpen,
  Calculator,
  Cat,
  ChevronDown,
  Dog,
  FlaskConical,
  HeartPulse,
  HelpCircle,
  ListChecks,
  LineChart,
  Shield,
  Moon,
  Plus,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FoodPresetPicker } from "@/components/ui/food-preset-picker";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tooltip } from "@/components/ui/tooltip";
import { catStatuses, dogStatuses, getResultRecommendation, references, statusLabels } from "@/data/content";
import { verifiedFoods, verifiedFoodsById, type VerifiedFood } from "@/data/verifiedFoods";
import { articlePath, articlesBySlug, articlesNewestFirst, formatPublished, type Article } from "@/data/articles";
import { staticPages, relatedForPage } from "@/data/pages";
import { articleJsonLd, calculatorJsonLd, homeJsonLd, resolveSeo } from "@/data/seo";
import { calculatorPageKeys, calculatorPages, type CalculatorPageKey } from "@/data/calculatorPages";
import {
  assessTargetWeight,
  calculateFeedingPlan,
  calculateWeightTrend,
  getFoodDensitySanityWarning,
  getWeightSanityWarning,
  kgToPounds,
  normalizeFoodDensity,
  type DensityUnit,
  type Species,
  type WeightTrendResult,
  type WeightUnit,
} from "@/utils/nutritionMath";
import { cn } from "@/lib/utils";

type CalculatorPageRoute = CalculatorPageKey;
type Page = "home" | "method" | "label-guide" | "articles" | "safety" | "privacy" | CalculatorPageRoute;
type RouteState = {
  page: Page;
  articleSlug?: string;
};
type SiteTheme = "light" | "dark";
type FoodEntryMode = "preset" | "manual";
type CalculatorResult = {
  weightKg: number;
  rer: number;
  targetApplied: boolean;
  dailyKcal: number;
  foodDensityKcalPerKg: number;
  measurements: {
    gramsPerDay: number;
    cupsPerDay: number | null;
    gramsPerMeal: number;
    cupsPerMeal: number | null;
  };
};
type FoodMix = {
  hasSecondFood: boolean;
  food1DensityKcalPerKg: number;
  food2DensityKcalPerKg: number | null;
  food1SharePercent: number;
};

const routePaths: Record<Page, string> = {
  home: "/",
  method: "/method/",
  "label-guide": "/label-guide/",
  articles: "/articles/",
  safety: "/safety/",
  privacy: "/privacy/",
  "dog-calorie-calculator": "/dog-calorie-calculator/",
  "cat-calorie-calculator": "/cat-calorie-calculator/",
};

// Header and mobile navigation. The species calculator pages are reached from the home page,
// method and label guide rather than the nav, which has no room on a phone.
const navPages = ["home", "method", "label-guide", "articles", "safety", "privacy"] as const;

const SHOW_AD_PLACEHOLDERS = false;
const DISCLAIMER_STORAGE_KEY = "foodbowlscience-disclaimer-dismissed";
// SEO source-of-truth (seoContent, homeJsonLd, resolveSeo, articleJsonLd) lives in src/data/seo.ts
// so the client Seo component and the build-time prerender generator can never diverge.

const pageTitles: Record<(typeof navPages)[number], string> = {
  home: "Calculator",
  method: "Method",
  "label-guide": "Label Guide",
  articles: "Articles",
  safety: "Safety",
  privacy: "Privacy",
};

const siteThemes: Array<{ value: SiteTheme; label: string }> = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const themeIcons: Record<SiteTheme, LucideIcon> = {
  light: Sun,
  dark: Moon,
};

const getAdjustmentHelper = (adjustmentPercent: number) => {
  if (adjustmentPercent < 0) {
    return "Shows a lower portion for monitoring, not a vet-prescribed diet. Use body condition and weekly weight checks.";
  }

  if (adjustmentPercent > 0) {
    return "Shows a higher portion and flags weight-gain monitoring. Increase only when weight or condition suggests it.";
  }

  return "Uses the daily calorie estimate from the selected life stage or goal.";
};

const lifeStageHelp: Record<string, string> = {
  neutered_adult: "Most adult pets who have been spayed or neutered. This is the best starting point for many pets.",
  intact_adult: "Adult pets who have not been spayed or neutered. They often need a little more energy.",
  obesity_prone: "For pets who gain weight easily or are less active, but are not on a formal weight-loss plan.",
  weight_loss:
    "For pets who need to lose weight. Use this as a starting estimate and check with your vet, especially for cats or pets with health problems.",
  puppy_under_4m: "Young puppies grow quickly and usually need more calories for their size.",
  puppy_over_4m: "Older puppies still need growth support, but usually less than very young puppies.",
  kitten: "Kittens are growing and usually need more calories for their size than adult cats.",
};

const isDetailPage = (route: string): route is "method" | "label-guide" | "articles" | "safety" | "privacy" =>
  route === "method" || route === "label-guide" || route === "articles" || route === "safety" || route === "privacy";

const isCalculatorPage = (route: string): route is CalculatorPageRoute => (calculatorPageKeys as string[]).includes(route);

const parseRoute = (): RouteState => {
  const legacyHashRoute = window.location.hash.startsWith("#/")
    ? window.location.hash.replace("#/", "").replace(/\/$/, "")
    : "";
  if (legacyHashRoute.startsWith("articles/")) {
    const slug = legacyHashRoute.replace(/^articles\//, "");
    if (articlesBySlug[slug]) return { page: "articles", articleSlug: slug };
  }
  if (legacyHashRoute === "research" || legacyHashRoute === "articles") return { page: "articles" };
  if (isDetailPage(legacyHashRoute)) return { page: legacyHashRoute };

  const route = window.location.pathname.replace(/^\/+|\/+$/g, "");
  if (route.startsWith("articles/")) {
    const slug = route.replace(/^articles\//, "");
    if (articlesBySlug[slug]) return { page: "articles", articleSlug: slug };
  }
  if (route === "research" || route === "articles") return { page: "articles" };
  if (isDetailPage(route)) return { page: route };
  if (isCalculatorPage(route)) return { page: route };
  return { page: "home" };
};

const getRouteHref = (route: RouteState) => (route.page === "articles" && route.articleSlug ? articlePath(route.articleSlug) : routePaths[route.page]);

const getMetaElement = (selector: string, createElement: () => HTMLMetaElement) => {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) return existing;
  const created = createElement();
  document.head.appendChild(created);
  return created;
};

const readStoredTheme = () => {
  try {
    const storedTheme = window.localStorage.getItem("foodbowlscience-theme");
    return storedTheme === "dark" || storedTheme === "light" ? storedTheme : "light";
  } catch {
    return "light";
  }
};

const readDisclaimerDismissed = () => {
  try {
    return window.localStorage.getItem(DISCLAIMER_STORAGE_KEY) === "dismissed";
  } catch {
    return false;
  }
};

const dismissDisclaimer = () => {
  try {
    window.localStorage.setItem(DISCLAIMER_STORAGE_KEY, "dismissed");
  } catch {
    // Best effort only; the footer can still close for the current session.
  }
};

function ChoiceToggle({
  value,
  options,
  onChange,
  className,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <div className={cn("grid gap-1 rounded-lg bg-slate-100 p-1.5", options.length === 2 && "grid-cols-2", className)}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            "rounded-md px-3 py-2 text-sm font-semibold transition-colors",
            value === option.value ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-950",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function StepSection({
  step,
  title,
  description,
  children,
}: {
  step: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="calculator-step">
      <div className="flex items-start gap-4">
        <span className="step-chip" aria-hidden="true">
          {step}
        </span>
        <div>
          <h3 className="step-title">{title}</h3>
          <p className="step-copy">{description}</p>
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}
function Seo({ route }: { route: RouteState }) {
  useEffect(() => {
    const article = route.articleSlug ? articlesBySlug[route.articleSlug] : undefined;
    const seo = resolveSeo(route.page, article);
    const ogImage = seo.ogImage;
    document.title = seo.title;

    getMetaElement('meta[name="description"]', () => {
      const element = document.createElement("meta");
      element.name = "description";
      return element;
    }).content = seo.description;

    getMetaElement('meta[property="og:title"]', () => {
      const element = document.createElement("meta");
      element.setAttribute("property", "og:title");
      return element;
    }).content = seo.title;

    getMetaElement('meta[property="og:description"]', () => {
      const element = document.createElement("meta");
      element.setAttribute("property", "og:description");
      return element;
    }).content = seo.description;

    getMetaElement('meta[property="og:url"]', () => {
      const element = document.createElement("meta");
      element.setAttribute("property", "og:url");
      return element;
    }).content = seo.canonical;

    getMetaElement('meta[property="og:type"]', () => {
      const element = document.createElement("meta");
      element.setAttribute("property", "og:type");
      return element;
    }).content = seo.ogType;

    getMetaElement('meta[property="og:image"]', () => {
      const element = document.createElement("meta");
      element.setAttribute("property", "og:image");
      return element;
    }).content = ogImage;

    getMetaElement('meta[property="og:image:alt"]', () => {
      const element = document.createElement("meta");
      element.setAttribute("property", "og:image:alt");
      return element;
    }).content = seo.ogImageAlt;

    getMetaElement('meta[name="twitter:title"]', () => {
      const element = document.createElement("meta");
      element.name = "twitter:title";
      return element;
    }).content = seo.title;

    getMetaElement('meta[name="twitter:description"]', () => {
      const element = document.createElement("meta");
      element.name = "twitter:description";
      return element;
    }).content = seo.description;

    getMetaElement('meta[name="twitter:image"]', () => {
      const element = document.createElement("meta");
      element.name = "twitter:image";
      return element;
    }).content = ogImage;

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = seo.canonical;

    const jsonLdId = "foodbowlscience-jsonld";
    const existingJsonLd = document.getElementById(jsonLdId);
    const pageJsonLd = route.page === "home" ? homeJsonLd : isCalculatorPage(route.page) ? calculatorJsonLd(route.page) : null;
    if (pageJsonLd) {
      const jsonLd = existingJsonLd instanceof HTMLScriptElement ? existingJsonLd : document.createElement("script");
      jsonLd.id = jsonLdId;
      jsonLd.type = "application/ld+json";
      jsonLd.textContent = JSON.stringify(pageJsonLd);
      if (!existingJsonLd) document.head.appendChild(jsonLd);
    } else if (article) {
      const jsonLd = existingJsonLd instanceof HTMLScriptElement ? existingJsonLd : document.createElement("script");
      jsonLd.id = jsonLdId;
      jsonLd.type = "application/ld+json";
      jsonLd.textContent = JSON.stringify(articleJsonLd(article));
      if (!existingJsonLd) document.head.appendChild(jsonLd);
    } else {
      existingJsonLd?.remove();
    }
  }, [route]);

  return null;
}

function App() {
  const [route, setRoute] = useState<RouteState>(() => parseRoute());
  const [showDisclaimer, setShowDisclaimer] = useState(() => !readDisclaimerDismissed());
  const [showSidebar, setShowSidebar] = useState(true);
  const [theme, setTheme] = useState<SiteTheme>(() => readStoredTheme());

  useEffect(() => {
    if (window.location.hash.startsWith("#/")) {
      const parsedRoute = parseRoute();
      window.history.replaceState(null, "", getRouteHref(parsedRoute));
      setRoute(parsedRoute);
    } else if (window.location.pathname.replace(/^\/+|\/+$/g, "") === "research") {
      const parsedRoute = parseRoute();
      window.history.replaceState(null, "", getRouteHref(parsedRoute));
      setRoute(parsedRoute);
    }

    const handleNavigation = () => setRoute(parseRoute());
    window.addEventListener("popstate", handleNavigation);

    // Client-side navigation: intercept internal link clicks so moving between pages
    // swaps the view in-app instead of doing a full reload. Without this, every link is
    // a fresh page load that re-runs the prerender -> React handoff (the "flash"). Only
    // plain left-clicks on same-origin, non-hash, non-new-tab links are handled; hash
    // anchors, downloads, modified clicks, and external links keep their default behavior.
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      const anchor = (event.target as HTMLElement | null)?.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || anchor.target === "_blank" || anchor.hasAttribute("download") || anchor.hasAttribute("data-native"))
        return;
      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin || url.hash) return; // external or in-page/anchor link
      if (url.pathname === window.location.pathname) return; // same page
      event.preventDefault();
      window.history.pushState(null, "", url.pathname);
      setRoute(parseRoute());
      window.scrollTo({ top: 0 });
    };
    document.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("popstate", handleNavigation);
      document.removeEventListener("click", handleClick);
    };
  }, []);

  // Accessibility for client-side navigation: a full page load resets focus and lets a
  // screen reader announce the new page. Since we now swap views in-app, do that manually
  // by moving focus to the new view's <main> landmark on each route change (skipping the
  // initial mount so we never steal focus on first load).
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    const main = document.querySelector<HTMLElement>("main");
    if (main) {
      main.setAttribute("tabindex", "-1");
      main.style.outline = "none"; // no visible ring for programmatic (non-keyboard) focus
      main.focus({ preventScroll: true });
    }
  }, [route]);

  useEffect(() => {
    try {
      window.localStorage.setItem("foodbowlscience-theme", theme);
    } catch {
      // Ignore storage failures and keep the theme in memory for this session.
    }
  }, [theme]);

  return (
    <div data-theme={theme} className={cn("site-shell min-h-screen bg-white pb-24 transition-[padding] duration-200", showSidebar && "lg:pl-64")}>
      <Seo route={route} />
      <Header page={route.page} theme={theme} onThemeChange={setTheme} showSidebar={showSidebar} onSidebarToggle={() => setShowSidebar(true)} />
      {showSidebar ? <SideNav page={route.page} onSidebarToggle={() => setShowSidebar(false)} /> : null}
      {route.page === "home" ? (
        <HomePage />
      ) : isCalculatorPage(route.page) ? (
        <CalculatorLandingPage pageKey={route.page} />
      ) : route.page === "articles" ? (
        route.articleSlug ? <ArticlePage article={articlesBySlug[route.articleSlug]} /> : <ArticlesHub />
      ) : (
        <DetailPage page={route.page} />
      )}
      {showDisclaimer ? (
        <DisclaimerFooter
          onClose={() => {
            dismissDisclaimer();
            setShowDisclaimer(false);
          }}
        />
      ) : null}
    </div>
  );
}

function Header({
  page,
  theme,
  onThemeChange,
  showSidebar,
  onSidebarToggle,
}: {
  page: Page;
  theme: SiteTheme;
  onThemeChange: (theme: SiteTheme) => void;
  showSidebar: boolean;
  onSidebarToggle: () => void;
}) {
  return (
    <header className={cn("sticky top-0 z-30 border-b bg-white/92 backdrop-blur lg:fixed lg:right-0", showSidebar ? "lg:left-64" : "lg:left-0")}>
      <div className="container flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onSidebarToggle}
            className={cn("hidden lg:inline-flex", showSidebar && "lg:hidden")}
            aria-label="Show sidebar menu"
          >
            <PanelLeftOpen aria-hidden="true" />
          </Button>
          <a href={routePaths.home} className="flex items-center gap-2 font-display text-lg font-extrabold tracking-normal text-slate-950">
            <BrandMark />
            <span>FoodBowlScience</span>
          </a>
        </div>
        <nav aria-label="Primary navigation" className="hidden items-center gap-1 md:flex lg:hidden">
          {navPages.map((item) => (
            <a
              key={item}
              href={routePaths[item]}
              className={cn(
                "rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950",
                page === item && "bg-slate-100 text-slate-950",
              )}
            >
              {pageTitles[item]}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <ThemeSwitcher theme={theme} onThemeChange={onThemeChange} />
          <Button size="sm" onClick={() => (window.location.href = isCalculatorPage(page) ? "#calculator" : "/#calculator")}>
            <Calculator data-icon="inline-start" />
            <span className="sr-only sm:not-sr-only">Start</span>
          </Button>
        </div>
      </div>
      <nav aria-label="Mobile navigation" className="flex gap-1 overflow-x-auto border-t px-4 py-2 md:hidden">
        {navPages.map((item) => (
          <a
            key={item}
            href={routePaths[item]}
            className={cn(
              "shrink-0 rounded-md px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950",
              page === item && "bg-slate-100 text-slate-950",
            )}
          >
            {pageTitles[item]}
          </a>
        ))}
      </nav>
    </header>
  );
}

function SideNav({ page, onSidebarToggle }: { page: Page; onSidebarToggle: () => void }) {
  const links: Array<{ page: Page; label: string; icon: LucideIcon; hint: string }> = [
    { page: "home", label: "Calculator", icon: Calculator, hint: "Daily feeding estimate" },
    { page: "method", label: "Method", icon: FlaskConical, hint: "How the math works" },
    { page: "label-guide", label: "Label guide", icon: ListChecks, hint: "EU, UK, and US labels" },
    { page: "articles", label: "Articles", icon: BookOpen, hint: "Owner guides and evidence" },
    { page: "safety", label: "Safety", icon: HelpCircle, hint: "When to call a vet" },
    { page: "privacy", label: "Privacy", icon: Shield, hint: "What data is handled" },
  ];

  return (
    <aside className="side-nav fixed left-0 top-0 z-20 hidden h-dvh w-64 flex-col border-r bg-slate-50 px-4 pb-6 pt-24 lg:flex">
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={onSidebarToggle}
        className="absolute right-0 top-4 hidden translate-x-1/2 bg-white shadow-sm lg:inline-flex"
        aria-label="Hide sidebar menu"
      >
        <PanelLeftClose aria-hidden="true" />
      </Button>
      <div className="mb-8 px-2">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Precision feeding</p>
        <h2 className="mt-2 font-display text-2xl font-bold text-slate-950">FoodBowlScience</h2>
        <p className="mt-1 text-sm leading-6 text-slate-600">Evidence-informed portions for real food labels.</p>
      </div>
      <nav className="flex flex-1 flex-col gap-2" aria-label="Sidebar navigation">
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <a
              key={link.page}
              href={routePaths[link.page]}
              className={cn(
                "group rounded-lg px-4 py-3 transition hover:bg-white",
                page === link.page ? "bg-blue-600 text-white shadow-sm" : "text-slate-600",
              )}
            >
              <span className="flex items-center gap-3 text-sm font-semibold">
                <Icon aria-hidden="true" />
                {link.label}
              </span>
              <span className={cn("mt-1 block pl-8 text-xs", page === link.page ? "text-blue-50" : "text-slate-500")}>
                {link.hint}
              </span>
            </a>
          );
        })}
      </nav>
    </aside>
  );
}

function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <svg viewBox="0 0 48 48" role="img">
        <defs>
          <linearGradient id="mark-gradient" x1="6" y1="6" x2="42" y2="42" gradientUnits="userSpaceOnUse">
            <stop stopColor="var(--theme-mark-1, #2563eb)" />
            <stop offset="1" stopColor="var(--theme-mark-2, #0ea5e9)" />
          </linearGradient>
        </defs>
        <path className="brand-mark-bg" d="M8 6h32a4 4 0 0 1 4 4v28a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V10a4 4 0 0 1 4-4Z" />
        <path className="brand-mark-bowl" d="M13 25h22c-.9 7.2-5.3 11-11 11s-10.1-3.8-11-11Z" />
        <path className="brand-mark-line" d="M15 25c2.1-3 5.2-4.6 9-4.6s6.9 1.6 9 4.6" />
        <path className="brand-mark-ear" d="M16 20 12 13l8 3" />
        <path className="brand-mark-ear" d="M32 20l4-7-8 3" />
        <circle className="brand-mark-dot" cx="20" cy="24" r="1.6" />
        <circle className="brand-mark-dot" cx="28" cy="24" r="1.6" />
        <path className="brand-mark-spark" d="M24 12v5M21.5 14.5h5" />
      </svg>
    </span>
  );
}

function ThemeSwitcher({ theme, onThemeChange }: { theme: SiteTheme; onThemeChange: (theme: SiteTheme) => void }) {
  return (
    <>
      <div className="hidden rounded-md border bg-white p-1 md:flex" aria-label="Display mode">
        {siteThemes.map((item) => {
          const Icon = themeIcons[item.value];
          return (
            <button
              key={item.value}
              type="button"
              aria-pressed={theme === item.value}
              onClick={() => onThemeChange(item.value)}
              className={cn(
                "inline-flex h-9 items-center gap-2 rounded px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950",
                theme === item.value && "bg-blue-600 text-white hover:bg-blue-600 hover:text-white",
              )}
            >
              <Icon aria-hidden="true" className="size-4" />
              {item.label}
            </button>
          );
        })}
      </div>
      <label className="sr-only" htmlFor="theme-switcher">
        Display mode
      </label>
      <select
        id="theme-switcher"
        value={theme}
        onChange={(event) => onThemeChange(event.target.value as SiteTheme)}
        className="h-9 rounded-md border bg-white px-2 text-xs font-semibold text-slate-700 md:hidden"
      >
        {siteThemes.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
    </>
  );
}

function HomePage() {
  return (
    <main className="pt-0 lg:pt-16">
      <section className="hero-section relative overflow-hidden border-b bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]">
        <div className="container flex flex-col gap-12 py-12 lg:py-20">
          <div className="hero-panel relative overflow-hidden rounded-xl border bg-slate-950 px-6 py-10 text-white shadow-clinical sm:px-10 lg:min-h-[430px] lg:px-14 lg:py-14">
            <img
              src="/hero-pets.jpg"
              alt="A dog and cat sitting beside food bowls in a warm home"
              className="absolute inset-0 h-full w-full object-cover"
              decoding="async"
            />
            <div className="absolute inset-0 bg-slate-950/70" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/88 to-slate-950/34" />
            <div className="relative z-10 max-w-3xl">
              <div className="hero-kicker mb-6 inline-flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-blue-100">
                <span className="h-px w-8 bg-blue-300" />
                Evidence-informed, label-specific estimates
              </div>
              <h1 className="font-display text-4xl font-bold leading-tight tracking-normal text-white sm:text-5xl lg:text-6xl">
                Scientifically Accurate Feeding Calculator for Dogs & Cats.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-blue-50/90">
                Get a practical daily feeding estimate, in grams or cups, from your pet's weight, age, goal, and the
                calories printed on the food label, whether you use EU/UK metric labels or US cup-based labels.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#calculator" className="rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-500">
                  Start calculation
                </a>
                <a href={routePaths.method} className="rounded-lg border border-white/30 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10">
                  View method
                </a>
              </div>
              <p className="mt-6 text-sm text-blue-50/80">
                Species guides:{" "}
                <a href={routePaths["dog-calorie-calculator"]} className="font-semibold text-white underline underline-offset-4">
                  dog calorie calculator
                </a>{" "}
                ·{" "}
                <a href={routePaths["cat-calorie-calculator"]} className="font-semibold text-white underline underline-offset-4">
                  cat calorie calculator
                </a>
              </p>
            </div>
          </div>
          <CalculatorPanel />
        </div>
      </section>

      {SHOW_AD_PLACEHOLDERS ? (
        <div className="container py-10">
          <AdSlot label="Google AdSense horizontal banner placement" />
        </div>
      ) : null}

      <TrustSections />
    </main>
  );
}

function MobileResultSummary({
  result,
  unit,
  meals,
  feedingAdjustmentPercent,
  foodMix,
}: {
  result: CalculatorResult | null;
  unit: WeightUnit;
  meals: number;
  feedingAdjustmentPercent: number;
  foodMix: FoodMix | null;
}) {
  if (!result) return null;

  const adjustedKcal = Math.round(result.dailyKcal * (1 + feedingAdjustmentPercent / 100));
  const hasTwoFoodPlan = Boolean(foodMix?.hasSecondFood && foodMix.food2DensityKcalPerKg);
  const food1Kcal = Math.round(adjustedKcal * (hasTwoFoodPlan ? (foodMix?.food1SharePercent ?? 100) / 100 : 1));
  const food2Kcal = adjustedKcal - food1Kcal;
  const food1Grams = Math.round((food1Kcal / result.foodDensityKcalPerKg) * 1000);
  const food2Grams =
    hasTwoFoodPlan && foodMix?.food2DensityKcalPerKg
      ? Math.round((food2Kcal / foodMix.food2DensityKcalPerKg) * 1000)
      : null;
  const totalGrams = hasTwoFoodPlan && food2Grams !== null ? food1Grams + food2Grams : food1Grams;
  const cupsPerDay =
    !hasTwoFoodPlan && result.measurements.cupsPerDay !== null
      ? Number((result.measurements.cupsPerDay * (1 + feedingAdjustmentPercent / 100)).toFixed(2))
      : null;
  const summary =
    hasTwoFoodPlan && food2Grams !== null
      ? `About ${totalGrams} g/day total`
      : unit === "kg" || cupsPerDay === null
        ? `About ${totalGrams} g/day`
        : `About ${cupsPerDay} cups/day`;

  return (
    <div className="sticky top-20 z-10 mb-5 lg:hidden">
      <div className="rounded-xl border bg-white/96 px-4 py-3 shadow-lg backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue-700">Current result</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{summary}</div>
          </div>
          <div className="text-right text-xs leading-5 text-slate-500">
            <div>{adjustedKcal} kcal/day</div>
            <div>{meals} meals</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CalculatorPanel({ initialSpecies = "dog" }: { initialSpecies?: Species } = {}) {
  const [species, setSpecies] = useState<Species>(initialSpecies);
  const [targetWeight, setTargetWeight] = useState("");
  const [weight, setWeight] = useState(initialSpecies === "cat" ? "10" : "30");
  const [weightUnit, setWeightUnit] = useState<WeightUnit>("lb");
  const [status, setStatus] = useState("neutered_adult");
  const [density, setDensity] = useState("3600");
  const [densityUnit, setDensityUnit] = useState<DensityUnit>("kcal_per_kg");
  const [hasSecondFood, setHasSecondFood] = useState(false);
  const [secondDensity, setSecondDensity] = useState("90");
  const [secondDensityUnit, setSecondDensityUnit] = useState<DensityUnit>("kcal_per_100g");
  const [food1SharePercent, setFood1SharePercent] = useState(50);
  const [showCupEstimate, setShowCupEstimate] = useState(false);
  const [kcalPerCup, setKcalPerCup] = useState("400");
  const [meals, setMeals] = useState(2);
  const [trendWeeks, setTrendWeeks] = useState(12);
  const [feedingAdjustmentPercent, setFeedingAdjustmentPercent] = useState(0);
  const [showFoodHelp, setShowFoodHelp] = useState(false);
  const [selectedFood1Id, setSelectedFood1Id] = useState<string | null>(null);
  const [selectedFood2Id, setSelectedFood2Id] = useState<string | null>(null);
  const [food1EntryMode, setFood1EntryMode] = useState<FoodEntryMode>("preset");
  const [food2EntryMode, setFood2EntryMode] = useState<FoodEntryMode>("preset");

  const statuses = species === "dog" ? dogStatuses : catStatuses;
  const speciesFoods = useMemo(() => verifiedFoods.filter((food) => food.species === species), [species]);
  const selectedFood1 = selectedFood1Id ? verifiedFoodsById.get(selectedFood1Id) ?? null : null;
  const selectedFood2 = selectedFood2Id ? verifiedFoodsById.get(selectedFood2Id) ?? null : null;

  useEffect(() => {
    const valid = statuses.some((item) => item.value === status);
    if (!valid) setStatus(statuses[0].value);
  }, [species, status, statuses]);

  const targetAssessment = assessTargetWeight(status, Number(weight), targetWeight, weightUnit);
  const result = useMemo(() => {
    const rawWeight = Number(weight);
    const enteredDensity = Number(density);
    const cupCalories = showCupEstimate && !hasSecondFood ? Number(kcalPerCup) : undefined;

    if (rawWeight <= 0 || enteredDensity <= 0) return null;

    try {
      return calculateFeedingPlan({
        species,
        status,
        weight: rawWeight,
        targetWeight: targetAssessment.target,
        weightUnit,
        foodDensity: enteredDensity,
        densityUnit,
        kcalPerCup: cupCalories,
        mealsPerDay: meals,
      });
    } catch (error) {
      console.error("Feeding calculation failed", error);
      return null;
    }
  }, [density, densityUnit, hasSecondFood, kcalPerCup, meals, showCupEstimate, species, status, targetAssessment.target, weight, weightUnit]);

  const recommendation = getResultRecommendation(species, status);
  const enteredWeight = Number(weight);
  const enteredDensity = Number(density);
  const weightWarning = getWeightSanityWarning(species, Number(weight), weightUnit);
  const densityWarning = getFoodDensitySanityWarning(Number(density), densityUnit);
  const secondDensityWarning = hasSecondFood ? getFoodDensitySanityWarning(Number(secondDensity), secondDensityUnit) : null;
  const secondDensityValue = Number(secondDensity);
  const validationMessages = [
    enteredWeight > 0 ? null : "your pet's current weight",
    enteredDensity > 0 ? null : "the calories for the main food",
    hasSecondFood && secondDensityValue <= 0 ? "the calories for the second food" : null,
  ].filter((message): message is string => Boolean(message));
  const foodMix: FoodMix | null = result
    ? {
        hasSecondFood,
        food1DensityKcalPerKg: result.foodDensityKcalPerKg,
        food2DensityKcalPerKg: hasSecondFood && secondDensityValue > 0 ? normalizeFoodDensity(secondDensityValue, secondDensityUnit) : null,
        food1SharePercent,
      }
    : null;
  const applyPrimaryFoodPreset = (foodId: string | null) => {
    setSelectedFood1Id(foodId);

    if (!foodId) return;

    const food = verifiedFoodsById.get(foodId);
    if (!food) return;

    setDensity(String(food.kcalPerKg));
    setDensityUnit("kcal_per_kg");
    setKcalPerCup(food.kcalPerCup ? String(food.kcalPerCup) : "");

    if (food.kcalPerCup) {
      if (!hasSecondFood) {
        setShowCupEstimate(true);
      }
    } else {
      setShowCupEstimate(false);
    }
  };
  const applySecondaryFoodPreset = (foodId: string | null) => {
    setSelectedFood2Id(foodId);

    if (!foodId) return;

    const food = verifiedFoodsById.get(foodId);
    if (!food) return;

    setSecondDensity(String(food.kcalPerKg));
    setSecondDensityUnit("kcal_per_kg");
  };
  const handleSpeciesChange = (nextSpecies: Species) => {
    setSpecies(nextSpecies);
    // Both reset here: a stale status got the wrong factor for a render, and a stale target was
    // reread in the new species' unit (a 5 kg cat target became 5 lb for a 30 lb dog).
    setStatus("neutered_adult");
    setTargetWeight("");
    setFood1EntryMode("preset");
    setFood2EntryMode("preset");
    setSelectedFood1Id(null);
    setSelectedFood2Id(null);
    if (nextSpecies === "cat") {
      setWeight("5");
      setWeightUnit("kg");
    } else {
      setWeight("30");
      setWeightUnit("lb");
    }
  };

  return (
    <div id="calculator" className="grid scroll-mt-24 gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
      <Card className="shadow-clinical">
        <CardHeader className="p-7 pb-5">
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Calculator className="text-blue-600" />
            Feeding calculator
          </CardTitle>
          <CardDescription>A guided starting point for daily portions using your pet's weight and this food label.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-7 p-7 pt-0">
          <MobileResultSummary
            result={result}
            unit={weightUnit}
            meals={meals}
            feedingAdjustmentPercent={feedingAdjustmentPercent}
            foodMix={foodMix}
          />

          <StepSection step="1" title="Tell us about your pet" description="Start with pet type, current weight, and the broad goal you want to feed for.">
            <div>
              <label className="mb-3 block text-base font-semibold text-slate-900">Pet type</label>
              <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1.5">
                {(["dog", "cat"] as Species[]).map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={cn(
                      "flex h-12 items-center justify-center gap-2 rounded-md text-base font-semibold capitalize transition-colors",
                      species === item ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-950",
                    )}
                    aria-pressed={species === item}
                    onClick={() => handleSpeciesChange(item)}
                  >
                    {item === "dog" ? <Dog /> : <Cat />}
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-[1fr_144px]">
              <Field label="Current weight">
                <Input
                  inputMode="decimal"
                  type="number"
                  min="0"
                  value={weight}
                  onChange={(event) => setWeight(event.target.value)}
                />
                {weightWarning ? (
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                    {weightWarning}
                  </p>
                ) : enteredWeight > 0 ? (
                  <p className="field-helper">Example: many adult dogs are entered in lb, while many cats are entered in kg.</p>
                ) : (
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                    Enter your pet&apos;s current weight, for example 30 lb or 5 kg.
                  </p>
                )}
              </Field>
              <Field label="Unit">
                <div className="grid grid-cols-2 gap-1 rounded-md border bg-white p-1.5">
                  {(["lb", "kg"] as WeightUnit[]).map((unit) => (
                    <button
                      key={unit}
                      type="button"
                      className={cn(
                        "h-10 rounded text-sm font-semibold uppercase transition-colors",
                        weightUnit === unit ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100",
                      )}
                      onClick={() => setWeightUnit(unit)}
                    >
                      {unit}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            <Field label="Life stage / goal">
              <Select value={status} onChange={(event) => setStatus(event.target.value)} options={statuses} />
              <p className="rounded-md bg-blue-50 px-4 py-3 text-sm leading-6 text-slate-700">{lifeStageHelp[status]}</p>
            </Field>
            {status === "weight_loss" ? (
              <Field label={`Target weight (${weightUnit}), optional`}>
                <Input
                  inputMode="decimal"
                  type="number"
                  min="0"
                  placeholder="The ideal weight your vet gave you"
                  value={targetWeight}
                  onChange={(event) => setTargetWeight(event.target.value)}
                />
                {targetAssessment.notice ? (
                  <p role="status" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                    {targetAssessment.notice}
                  </p>
                ) : null}
                <p className="field-helper">
                  Veterinary weight-loss plans work out calories from your pet&apos;s ideal weight, not its current weight. Enter the target weight your vet gave you. If you leave it blank, this estimate uses current weight and will come out higher than your vet&apos;s plan.
                </p>
              </Field>
            ) : null}
          </StepSection>

          <StepSection step="2" title="Choose the food" description="Pick a popular food or type the calories from your package. You can always adjust the numbers manually.">
            <FoodPlanFields
              species={species}
              foods={speciesFoods}
              density={density}
              densityUnit={densityUnit}
              densityWarning={densityWarning}
              hasSecondFood={hasSecondFood}
              secondDensity={secondDensity}
              secondDensityUnit={secondDensityUnit}
              secondDensityWarning={secondDensityWarning}
              food1SharePercent={food1SharePercent}
              selectedFood1={selectedFood1}
              selectedFood2={selectedFood2}
              food1EntryMode={food1EntryMode}
              food2EntryMode={food2EntryMode}
              onDensityChange={setDensity}
              onDensityUnitChange={setDensityUnit}
              onSecondDensityChange={setSecondDensity}
              onSecondDensityUnitChange={setSecondDensityUnit}
              onFood1ShareChange={setFood1SharePercent}
              onFood1EntryModeChange={(mode) => setFood1EntryMode(mode)}
              onFood2EntryModeChange={(mode) => setFood2EntryMode(mode)}
              onFood1Select={applyPrimaryFoodPreset}
              onFood2Select={applySecondaryFoodPreset}
              onSecondFoodToggle={(nextMode) => {
                const useTwoFoods = nextMode === "two";
                setHasSecondFood(useTwoFoods);
                if (!useTwoFoods) {
                  setSelectedFood2Id(null);
                  setFood2EntryMode("preset");
                }
              }}
              onRemoveSecondFood={() => {
                setHasSecondFood(false);
                setSelectedFood2Id(null);
                setFood2EntryMode("preset");
              }}
            />
            {!hasSecondFood && !showCupEstimate ? (
              <div className="soft-panel">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm leading-6 text-slate-600">
                    Cups are optional. Use them only if your label gives a kcal/cup number.
                  </p>
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowCupEstimate(true)}>
                    <Plus data-icon="inline-start" />
                    Add cup estimate
                  </Button>
                </div>
              </div>
            ) : null}

            {!hasSecondFood && showCupEstimate ? (
              <Field
                label="Optional cup estimate"
                tooltip="Grams come from kcal/kg or kcal/100g. This field is only used if you also want a cup estimate for one food."
              >
                <Input inputMode="numeric" type="number" min="1" value={kcalPerCup} onChange={(event) => setKcalPerCup(event.target.value)} />
                <p className="rounded-md bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
                  Leave this only if your label lists kcal/cup. It does not affect grams; it only turns the calorie
                  target into cups.
                </p>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowCupEstimate(false)}>
                  Hide cup estimate
                </Button>
              </Field>
            ) : hasSecondFood ? (
              <div className="rounded-lg bg-blue-50 p-4 text-sm leading-6 text-slate-700">
                Mixed-food plans use grams only. Cup estimates would need a separate kcal/cup value for each food, so
                grams are clearer and safer here.
              </div>
            ) : null}

            <div>
              <button
                type="button"
                onClick={() => setShowFoodHelp((current) => !current)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700"
              >
                Need help reading the label?
                <ChevronDown className={cn("size-4 transition-transform", showFoodHelp && "rotate-180")} aria-hidden="true" />
              </button>
              {showFoodHelp ? (
                <div className="mt-3 rounded-lg border bg-slate-50 p-4">
                  <h3 className="text-sm font-semibold text-slate-900">What the food numbers do</h3>
                  <div className="mt-3 grid gap-3 text-sm leading-6 text-slate-600 md:grid-cols-2">
                    <div>
                      <span className="font-semibold text-slate-900">kcal/kg or kcal/100g</span> gives the gram amount.
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">kcal/cup</span> is only used if you want a cup estimate for one food.
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </StepSection>

          <StepSection step="3" title="Set the feeding plan" description="Choose how many meals you serve each day. The result updates instantly.">
            <Field label={`Meals per day: ${meals}`}>
              <Slider value={meals} min={1} max={6} step={1} onValueChange={setMeals} aria-label="Meals per day" />
              <p className="field-helper">This splits the daily amount into equal meals so the plan is easier to follow.</p>
            </Field>
          </StepSection>

        </CardContent>
      </Card>

      <OutputCard
        result={result}
        species={species}
        status={status}
        recommendation={recommendation}
        meals={meals}
        unit={weightUnit}
        trendWeeks={trendWeeks}
        onTrendWeeksChange={setTrendWeeks}
        feedingAdjustmentPercent={feedingAdjustmentPercent}
        onFeedingAdjustmentChange={setFeedingAdjustmentPercent}
        foodMix={foodMix}
        validationMessages={validationMessages}
      />
    </div>
  );
}

function Field({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-2.5">
      <span className="flex items-center gap-2 text-base font-semibold text-slate-900">
        {label}
        {tooltip ? <Tooltip label={tooltip} /> : null}
      </span>
      {children}
    </label>
  );
}

function FoodPlanFields({
  species,
  foods,
  density,
  densityUnit,
  densityWarning,
  hasSecondFood,
  secondDensity,
  secondDensityUnit,
  secondDensityWarning,
  food1SharePercent,
  selectedFood1,
  selectedFood2,
  food1EntryMode,
  food2EntryMode,
  onDensityChange,
  onDensityUnitChange,
  onSecondDensityChange,
  onSecondDensityUnitChange,
  onFood1ShareChange,
  onFood1EntryModeChange,
  onFood2EntryModeChange,
  onFood1Select,
  onFood2Select,
  onSecondFoodToggle,
  onRemoveSecondFood,
}: {
  species: Species;
  foods: readonly VerifiedFood[];
  density: string;
  densityUnit: DensityUnit;
  densityWarning: string | null;
  hasSecondFood: boolean;
  secondDensity: string;
  secondDensityUnit: DensityUnit;
  secondDensityWarning: string | null;
  food1SharePercent: number;
  selectedFood1: VerifiedFood | null;
  selectedFood2: VerifiedFood | null;
  food1EntryMode: FoodEntryMode;
  food2EntryMode: FoodEntryMode;
  onDensityChange: (value: string) => void;
  onDensityUnitChange: (value: DensityUnit) => void;
  onSecondDensityChange: (value: string) => void;
  onSecondDensityUnitChange: (value: DensityUnit) => void;
  onFood1ShareChange: (value: number) => void;
  onFood1EntryModeChange: (mode: FoodEntryMode) => void;
  onFood2EntryModeChange: (mode: FoodEntryMode) => void;
  onFood1Select: (foodId: string | null) => void;
  onFood2Select: (foodId: string | null) => void;
  onSecondFoodToggle: (value: "one" | "two") => void;
  onRemoveSecondFood: () => void;
}) {
  const densityOptions = [
    { value: "kcal_per_kg", label: "kcal/kg" },
    { value: "kcal_per_100g", label: "kcal/100g" },
  ];

  const renderManualFields = ({
    densityValue,
    selectedUnit,
    warning,
    onDensityValueChange,
    onDensityUnitValueChange,
    compact,
  }: {
    densityValue: string;
    selectedUnit: DensityUnit;
    warning: string | null;
    onDensityValueChange: (value: string) => void;
    onDensityUnitValueChange: (value: DensityUnit) => void;
    compact: boolean;
  }) => (
    <div className={cn("rounded-lg border p-4", compact ? "bg-slate-50/80" : "bg-white")}>
      <div className="mb-3">
        <div className="text-sm font-semibold text-slate-900">
          {compact ? "Adjust label values if needed" : "Enter calories from the label"}
        </div>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          {compact
            ? "Preset values are filled in. Edit them only if your package shows different calories."
            : "Use the calorie value printed on your package, such as 3600 kcal/kg or 90 kcal/100g."}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
        <Input
          inputMode="numeric"
          type="number"
          min="1"
          value={densityValue}
          onChange={(event) => onDensityValueChange(event.target.value)}
        />
        <Select
          value={selectedUnit}
          onChange={(event) => onDensityUnitValueChange(event.target.value as DensityUnit)}
          options={densityOptions}
        />
      </div>
      {warning ? (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">{warning}</p>
      ) : (
        <p className="mt-3 field-helper">Examples: many dry foods use kcal/kg, while many wet foods list kcal/100g.</p>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-slate-50 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
              Food label calories
              <Tooltip label="Use kcal/kg or kcal/100g from the food package. This is the value used to calculate grams." />
            </div>
            <p className="mt-1 text-sm leading-6 text-slate-600">Start with one food, or switch to a mixed-feeding plan if you serve two.</p>
          </div>
        </div>
        <div className="mt-4 max-w-sm">
          <ChoiceToggle
            value={hasSecondFood ? "two" : "one"}
            onChange={(value) => onSecondFoodToggle(value as "one" | "two")}
            options={[
              { value: "one", label: "One food" },
              { value: "two", label: "Two foods" },
            ]}
          />
        </div>
      </div>

      <div className={cn("grid gap-4", hasSecondFood && "lg:grid-cols-2")}>
        <div className="rounded-lg border bg-white p-4">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900">{hasSecondFood ? "Food 1" : "Food"}</div>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                {food1EntryMode === "preset" ? "Choose a popular food first, then fine-tune if needed." : "Use the calories printed on your package."}
              </p>
            </div>
            <div className="w-full sm:max-w-[270px]">
              <ChoiceToggle
                value={food1EntryMode}
                onChange={(value) => onFood1EntryModeChange(value as FoodEntryMode)}
                options={[
                  { value: "preset", label: "Popular food" },
                  { value: "manual", label: "Manual label entry" },
                ]}
              />
            </div>
          </div>

          {food1EntryMode === "preset" ? (
            <div className="space-y-4">
              <FoodPresetPicker species={species} value={selectedFood1} foods={foods} onSelect={onFood1Select} />
              {selectedFood1 ? (
                renderManualFields({
                  densityValue: density,
                  selectedUnit: densityUnit,
                  warning: densityWarning,
                  onDensityValueChange: onDensityChange,
                  onDensityUnitValueChange: onDensityUnitChange,
                  compact: true,
                })
              ) : (
                <div className="rounded-lg border border-dashed px-4 py-4 text-sm leading-6 text-slate-600">
                  Choose a preset above for the fastest start, or switch to manual label entry if your food is not listed.
                </div>
              )}
            </div>
          ) : (
            renderManualFields({
              densityValue: density,
              selectedUnit: densityUnit,
              warning: densityWarning,
              onDensityValueChange: onDensityChange,
              onDensityUnitValueChange: onDensityUnitChange,
              compact: false,
            })
          )}
        </div>

        {hasSecondFood ? (
          <div className="rounded-lg border bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Food 2</div>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  {food2EntryMode === "preset" ? "Choose a second food or switch to manual entry." : "Enter the second food exactly as the label shows it."}
                </p>
              </div>
              <button
                type="button"
                onClick={onRemoveSecondFood}
                className="inline-flex size-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                aria-label="Remove second food"
              >
                <Trash2 aria-hidden="true" className="size-4" />
              </button>
            </div>
            <div className="mb-4">
              <ChoiceToggle
                value={food2EntryMode}
                onChange={(value) => onFood2EntryModeChange(value as FoodEntryMode)}
                options={[
                  { value: "preset", label: "Popular food" },
                  { value: "manual", label: "Manual label entry" },
                ]}
              />
            </div>
            {food2EntryMode === "preset" ? (
              <div className="space-y-4">
                <FoodPresetPicker species={species} value={selectedFood2} foods={foods} onSelect={onFood2Select} />
                {selectedFood2 ? (
                  renderManualFields({
                    densityValue: secondDensity,
                    selectedUnit: secondDensityUnit,
                    warning: secondDensityWarning,
                    onDensityValueChange: onSecondDensityChange,
                    onDensityUnitValueChange: onSecondDensityUnitChange,
                    compact: true,
                  })
                ) : (
                  <div className="rounded-lg border border-dashed px-4 py-4 text-sm leading-6 text-slate-600">
                    Pick a second preset or switch to manual label entry if you want to type the package values yourself.
                  </div>
                )}
              </div>
            ) : (
              renderManualFields({
                densityValue: secondDensity,
                selectedUnit: secondDensityUnit,
                warning: secondDensityWarning,
                onDensityValueChange: onSecondDensityChange,
                onDensityUnitValueChange: onSecondDensityUnitChange,
                compact: false,
              })
            )}
          </div>
        ) : null}
      </div>

      {hasSecondFood ? (
        <div className="mt-4 rounded-lg bg-slate-50 p-4">
          <div className="mb-2 flex items-center justify-between gap-3 text-sm font-semibold text-slate-900">
            <span>How much of each food?</span>
            <span>
              Food 1 {food1SharePercent}% / Food 2 {100 - food1SharePercent}%
            </span>
          </div>
          <Slider value={food1SharePercent} min={10} max={90} step={5} onValueChange={onFood1ShareChange} aria-label="Food 1 share of the daily food plan" />
          <div className="mt-3 flex flex-wrap gap-2">
            {[50, 75, 25].map((share) => (
              <Button
                key={share}
                type="button"
                size="sm"
                variant={food1SharePercent === share ? "default" : "outline"}
                onClick={() => onFood1ShareChange(share)}
              >
                {share}/{100 - share}
              </Button>
            ))}
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Set the share of your pet's daily food plan. Use 50/50 for an even split, or move the slider if one food is
            meant to be the main food and the other is a smaller part of the day.
          </p>
        </div>
      ) : (
        <p className="rounded-md bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
          This is the main number. It tells the calculator how many grams of this exact food match your pet's daily
          calories. Dry and wet foods can be very different.
        </p>
      )}
    </div>
  );
}

function OutputCard({
  result,
  species,
  status,
  recommendation,
  meals,
  unit,
  trendWeeks,
  onTrendWeeksChange,
  feedingAdjustmentPercent,
  onFeedingAdjustmentChange,
  foodMix,
  validationMessages,
}: {
  result: CalculatorResult | null;
  species: Species;
  status: string;
  recommendation: { title: string; text: string; affiliateUrl?: string };
  meals: number;
  unit: WeightUnit;
  trendWeeks: number;
  onTrendWeeksChange: (weeks: number) => void;
  feedingAdjustmentPercent: number;
  onFeedingAdjustmentChange: (adjustmentPercent: number) => void;
  foodMix: FoodMix | null;
  validationMessages: string[];
}) {
  const adjustedKcal = result ? Math.round(result.dailyKcal * (1 + feedingAdjustmentPercent / 100)) : null;
  const hasTwoFoodPlan = Boolean(foodMix?.hasSecondFood && foodMix.food2DensityKcalPerKg && adjustedKcal);
  const food1Kcal = adjustedKcal ? Math.round(adjustedKcal * (hasTwoFoodPlan ? (foodMix?.food1SharePercent ?? 100) / 100 : 1)) : 0;
  const food2Kcal = adjustedKcal ? adjustedKcal - food1Kcal : 0;
  const food1Grams = foodMix ? Math.round((food1Kcal / foodMix.food1DensityKcalPerKg) * 1000) : null;
  const food2Grams = hasTwoFoodPlan && foodMix?.food2DensityKcalPerKg ? Math.round((food2Kcal / foodMix.food2DensityKcalPerKg) * 1000) : null;
  const food1GramsPerMeal = food1Grams === null ? null : Math.round(food1Grams / meals);
  const food2GramsPerMeal = food2Grams === null ? null : Math.round(food2Grams / meals);
  const food1KcalPerMeal = Math.round(food1Kcal / meals);
  const food2KcalPerMeal = Math.round(food2Kcal / meals);
  const adjustedMeasurements =
    result && adjustedKcal && food1Grams !== null
      ? {
          gramsPerDay: hasTwoFoodPlan && food2Grams !== null ? food1Grams + food2Grams : food1Grams,
          cupsPerDay:
            hasTwoFoodPlan || result.measurements.cupsPerDay === null
              ? null
              : Number((result.measurements.cupsPerDay * (1 + feedingAdjustmentPercent / 100)).toFixed(2)),
          gramsPerMeal: Math.round((hasTwoFoodPlan && food2Grams !== null ? food1Grams + food2Grams : food1Grams) / meals),
          cupsPerMeal:
            hasTwoFoodPlan || result.measurements.cupsPerMeal === null
              ? null
              : Number((result.measurements.cupsPerMeal * (1 + feedingAdjustmentPercent / 100)).toFixed(2)),
        }
      : null;
  const cups = adjustedMeasurements?.cupsPerDay;
  const grams = adjustedMeasurements?.gramsPerDay;
  const showMetric = unit === "kg";
  const mealAmount = hasTwoFoodPlan
    ? "see food split"
    : showMetric
      ? `${adjustedMeasurements?.gramsPerMeal} g`
      : `${adjustedMeasurements?.cupsPerMeal ?? ""} cups`;
  const weightDisplay = showMetric
    ? `${result?.weightKg.toFixed(1)} kg`
    : `${kgToPounds(result?.weightKg ?? 0).toFixed(1)} lb`;
  const portionDelta = result && adjustedMeasurements ? adjustedMeasurements.gramsPerDay - result.measurements.gramsPerDay : 0;
  const portionDeltaDisplay = showMetric
    ? `${portionDelta > 0 ? "+" : ""}${portionDelta} g/day`
    : adjustedMeasurements?.cupsPerDay !== null && result?.measurements.cupsPerDay !== null
      ? `${Number(((adjustedMeasurements?.cupsPerDay ?? 0) - (result?.measurements.cupsPerDay ?? 0)).toFixed(2)) > 0 ? "+" : ""}${Number(((adjustedMeasurements?.cupsPerDay ?? 0) - (result?.measurements.cupsPerDay ?? 0)).toFixed(2))} cups/day`
      : `${portionDelta > 0 ? "+" : ""}${portionDelta} g/day`;
  const kcalPerKgBodyWeight = result && adjustedKcal ? Math.round(adjustedKcal / result.weightKg) : null;
  const secondStatValue =
    feedingAdjustmentPercent === 0 && result
      ? `${kcalPerKgBodyWeight} kcal/kg/day`
      : portionDeltaDisplay;
  const primaryHeadline =
    hasTwoFoodPlan && food2Grams !== null
      ? `Feed about ${adjustedMeasurements?.gramsPerDay} g total per day`
      : showMetric || !cups
        ? `Feed about ${grams} g per day`
        : `Feed about ${cups} cups per day`;
  const primarySupporting =
    hasTwoFoodPlan && food2Grams !== null
      ? `${adjustedKcal} kcal/day across both foods. The split is shown below.`
      : showMetric || !cups
        ? `${adjustedKcal} kcal/day based on the selected label calories.`
        : `${adjustedKcal} kcal/day. Gram-based details are still shown in the meal breakdown below.`;
  const trend = result ? calculateWeightTrend(species, status, result.weightKg, trendWeeks, feedingAdjustmentPercent, result.dailyKcal) : null;
  const trendEnd = trend?.points.at(-1);
  const projectedWeightDisplay =
    trend?.applies && trendEnd
      ? formatWeightRange(trendEnd.assertiveKg, trendEnd.conservativeKg, unit)
      : weightDisplay;

  return (
    <Card className="border-blue-100 bg-slate-950 text-white shadow-clinical lg:sticky lg:top-24 lg:self-start">
      <CardHeader className="p-7 pb-5">
        <CardTitle className="flex items-center gap-2 text-2xl text-white">
          <Activity className="text-blue-300" />
          Daily feeding guide
        </CardTitle>
        <CardDescription className="text-slate-300">
          {result ? `${statusLabels[status]} starting estimate` : "Add the key inputs on the left to see a feeding guide."}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-7 p-7 pt-0">
        {result ? (
          <>
            <div className="rounded-xl bg-white/10 p-5">
              <div className="text-sm font-semibold uppercase tracking-[0.14em] text-blue-200">Best starting point</div>
              <div className="mt-3 text-3xl font-semibold tracking-normal text-white sm:text-4xl">{primaryHeadline}</div>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                {primarySupporting}
                {feedingAdjustmentPercent !== 0 ? ` This includes a ${feedingAdjustmentPercent > 0 ? "+" : ""}${feedingAdjustmentPercent}% adjustment.` : ""}
              </p>
            </div>

            {hasTwoFoodPlan && food2Grams !== null ? (
              <div className="rounded-xl bg-white/10 p-4">
                <div className="text-sm font-semibold text-slate-300">Daily split</div>
                <div className="mt-4 grid gap-2">
                  <div className="rounded-lg bg-slate-950/45 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-300">Food 1</span>
                      <span className="text-2xl font-semibold text-white">{food1Grams} g</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-400">for the whole day</div>
                  </div>
                  <div className="rounded-lg bg-slate-950/45 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-300">Food 2</span>
                      <span className="text-2xl font-semibold text-white">{food2Grams} g</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-400">for the whole day</div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <MiniStat
                label={result.targetApplied ? "Resting calories at target" : "Resting calories"}
                value={`${result.rer} kcal`}
                help={
                  result.targetApplied
                    ? "RER means Resting Energy Requirement. For weight loss it is worked out at the target weight you entered, as veterinary weight-loss plans do."
                    : "RER means Resting Energy Requirement: an estimate of calories your pet would use at rest before life-stage adjustments."
                }
              />
              <MiniStat
                label={feedingAdjustmentPercent === 0 ? "Energy level of this plan" : "Portion change"}
                value={feedingAdjustmentPercent === 0 ? secondStatValue : portionDeltaDisplay}
                help={
                  feedingAdjustmentPercent === 0
                    ? "Daily calories divided by body weight. This gives a rough sense of how energy-dense the plan is for your pet's size."
                    : "How much the selected adjustment changes the daily portion compared with the calculated amount."
                }
              />
            </div>

            <div className="rounded-lg bg-white/10 p-5">
              <div className="mb-4 flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                <span className="font-semibold text-white">{hasTwoFoodPlan ? "At each meal" : "Meal breakdown"}</span>
                <span className="text-slate-300">
                  {meals} meals | {hasTwoFoodPlan ? "split below" : `${mealAmount} each`}
                </span>
              </div>
              <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${meals}, minmax(0, 1fr))` }}>
                {Array.from({ length: meals }).map((_, index) => (
                  <div key={index} className="h-3 rounded-full bg-blue-400" />
                ))}
              </div>
              {hasTwoFoodPlan && food2Grams !== null ? (
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <div className="rounded-lg bg-white/10 p-4">
                    <span className="block text-slate-400">Food 1 per meal</span>
                    <span className="mt-1 block text-2xl font-semibold text-white">
                      {food1GramsPerMeal} g
                    </span>
                    <span className="mt-1 block text-xs text-slate-400">{food1KcalPerMeal} kcal</span>
                  </div>
                  <div className="rounded-lg bg-white/10 p-4">
                    <span className="block text-slate-400">Food 2 per meal</span>
                    <span className="mt-1 block text-2xl font-semibold text-white">
                      {food2GramsPerMeal} g
                    </span>
                    <span className="mt-1 block text-xs text-slate-400">{food2KcalPerMeal} kcal</span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="space-y-5 rounded-lg border border-white/10 bg-white/5 px-5 py-5">
              <div>
                <div className="text-sm font-semibold text-white">Adjust and monitor</div>
                <div className="mt-1 text-xs leading-5 text-slate-400">
                  Fine-tune the portion by 5% and track the expected weight trend week by week.
                </div>
              </div>
              <ScenarioControls value={feedingAdjustmentPercent} onChange={onFeedingAdjustmentChange} />
              {trend ? <WeightTrendCard trend={trend} unit={unit} weeks={trendWeeks} onWeeksChange={onTrendWeeksChange} /> : null}
              <MiniStat
                label={trend?.applies ? `Week ${trendEnd?.week ?? trendWeeks}` : "Current weight reference"}
                value={trend?.applies ? projectedWeightDisplay : weightDisplay}
                help={
                  trend?.applies
                    ? "Estimated monitoring range after the selected number of weeks if progress follows the guideline pace."
                    : "This shows the current displayed body weight in the selected unit."
                }
              />
            </div>

            {recommendation.affiliateUrl ? <ResultGuidance recommendation={recommendation} /> : null}
          </>
        ) : (
          <div className="rounded-lg bg-white/10 p-6 text-slate-200">
            {validationMessages.length ? (
              <>
                <div className="text-base font-semibold text-white">We&apos;re missing a couple of details.</div>
                <p className="mt-2 text-sm leading-6 text-slate-300">
                  Add {validationMessages.join(" and ")} to see a daily feeding guide.
                </p>
              </>
            ) : (
              <>
                <div className="text-base font-semibold text-white">This combination could not be calculated.</div>
                <p className="mt-2 text-sm leading-6 text-slate-300">Check the weights and food calories you entered.</p>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value, help }: { label: string; value: string; help?: string }) {
  return (
    <div className="rounded-lg bg-white/10 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase text-blue-200">
        {label}
        {help ? <Tooltip label={help} /> : null}
      </div>
      <div className="mt-1 text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

function ScenarioControls({
  value,
  onChange,
}: {
  value: number;
  onChange: (adjustmentPercent: number) => void;
}) {
  const decreaseDisabled = value <= -30;
  const increaseDisabled = value >= 30;
  const label = value === 0 ? "Calculated amount" : `${value > 0 ? "+" : ""}${value}% food`;

  return (
    <div className="rounded-lg bg-white/10 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">Food amount adjustment</div>
          <div className="mt-1 text-xs text-slate-400">Change in 5% steps</div>
        </div>
        <div className="rounded-md bg-slate-950/70 px-3 py-2 text-right">
          <div className="text-xs font-semibold uppercase text-blue-200">Selected</div>
          <div className="text-sm font-semibold text-white">{label}</div>
        </div>
      </div>
      <div className="grid grid-cols-[1fr_1.25fr_1fr] gap-2">
        <button
          type="button"
          disabled={decreaseDisabled}
          onClick={() => onChange(Math.max(-30, value - 5))}
          className="min-h-11 rounded-md bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
        >
          -5%
        </button>
        <button
          type="button"
          aria-pressed={value === 0}
          onClick={() => onChange(0)}
          className={cn(
            "min-h-11 rounded-md px-3 py-2 text-xs font-semibold transition",
            value === 0 ? "bg-blue-500 text-white" : "bg-slate-950/60 text-slate-200 hover:bg-white/10",
          )}
        >
          Calculated
        </button>
        <button
          type="button"
          disabled={increaseDisabled}
          onClick={() => onChange(Math.min(30, value + 5))}
          className="min-h-11 rounded-md bg-slate-950/60 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
        >
          +5%
        </button>
      </div>
      <Slider value={value} min={-30} max={30} step={5} onValueChange={onChange} aria-label="Food amount adjustment percent" />
      <p className="mt-3 text-xs leading-5 text-slate-400">{getAdjustmentHelper(value)}</p>
    </div>
  );
}

function formatWeightRange(firstKg: number, secondKg: number, unit: WeightUnit) {
  const lowKg = Math.min(firstKg, secondKg);
  const highKg = Math.max(firstKg, secondKg);

  if (Math.abs(highKg - lowKg) < 0.05) {
    return unit === "kg" ? `${lowKg.toFixed(1)} kg` : `${kgToPounds(lowKg).toFixed(1)} lb`;
  }

  return unit === "kg"
    ? `${lowKg.toFixed(1)}-${highKg.toFixed(1)} kg`
    : `${kgToPounds(lowKg).toFixed(1)}-${kgToPounds(highKg).toFixed(1)} lb`;
}

function WeightTrendCard({
  trend,
  unit,
  weeks,
  onWeeksChange,
}: {
  trend: WeightTrendResult;
  unit: WeightUnit;
  weeks: number;
  onWeeksChange: (weeks: number) => void;
}) {
  const width = 320;
  const height = 170;
  const padding = 24;
  const displayValue = (kg: number) => (unit === "kg" ? `${kg.toFixed(1)} kg` : `${kgToPounds(kg).toFixed(1)} lb`);
  const values = trend.points.flatMap((point) => [point.conservativeKg, point.assertiveKg]);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = Math.max(maxValue - minValue, 0.1);
  const xForWeek = (week: number) => padding + (week / Math.max(trend.points.length - 1, 1)) * (width - padding * 2);
  const yForKg = (kg: number) => padding + ((maxValue - kg) / range) * (height - padding * 2);
  const linePath = (key: "conservativeKg" | "assertiveKg") =>
    trend.points.map((point, index) => `${index === 0 ? "M" : "L"} ${xForWeek(point.week).toFixed(1)} ${yForKg(point[key]).toFixed(1)}`).join(" ");
  const bandPath = [
    ...trend.points.map((point, index) => `${index === 0 ? "M" : "L"} ${xForWeek(point.week).toFixed(1)} ${yForKg(point.conservativeKg).toFixed(1)}`),
    ...trend.points
      .slice()
      .reverse()
      .map((point) => `L ${xForWeek(point.week).toFixed(1)} ${yForKg(point.assertiveKg).toFixed(1)}`),
    "Z",
  ].join(" ");
  const start = trend.points[0];
  const end = trend.points[trend.points.length - 1];
  const expectedRange = formatWeightRange(end.assertiveKg, end.conservativeKg, unit);
  const trendTitle =
    trend.model === "calorie_balance"
      ? `Rough ${trend.direction === "up" ? "gain" : "loss"} estimate after ${end.week} weeks`
      : trend.direction === "down"
        ? `Possible lower-weight range after ${end.week} weeks`
        : trend.direction === "up"
          ? `Higher-food monitoring over ${end.week} weeks`
          : `Weight monitoring over ${end.week} weeks`;
  const trendLead =
    trend.model === "calorie_balance"
      ? `${expectedRange} using the selected calorie change.`
      : trend.direction === "down"
        ? `${expectedRange} if progress follows a safe monitoring pace.`
        : trend.direction === "up"
          ? "Higher food can move weight upward; use weigh-ins to confirm the trend."
          : "Use weekly weigh-ins to confirm whether this portion holds weight steady.";

  return (
    <div className="rounded-lg bg-white/10 p-5">
      <div className="flex items-start gap-3">
        <LineChart className="mt-1 shrink-0 text-blue-300" aria-hidden="true" />
        <div>
          <h3 className="font-semibold text-white">{trendTitle}</h3>
          <p className="mt-1 text-sm leading-6 text-slate-300">{trendLead}</p>
        </div>
      </div>
      <div className="mt-4 rounded-md bg-white/10 p-3">
        <div className="mb-2 flex items-center justify-between gap-3 text-xs font-semibold text-slate-300">
          <span>Trend window</span>
          <span className="text-white">{weeks} weeks</span>
        </div>
        <Slider value={weeks} min={1} max={12} step={1} onValueChange={onWeeksChange} aria-label="Weight trend weeks" />
      </div>
      <div className="mt-4 overflow-hidden rounded-lg bg-slate-950/60 p-3">
        <svg
          className="h-auto w-full"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={`${trend.title}: ${displayValue(start.conservativeKg)} now and ${displayValue(end.assertiveKg)} to ${displayValue(end.conservativeKg)} after ${end.week} weeks`}
        >
          <line x1={padding} y1={padding} x2={padding} y2={height - padding} className="trend-axis" />
          <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} className="trend-axis" />
          <path d={bandPath} className={trend.applies ? "trend-band" : "trend-band-muted"} />
          <path d={linePath("conservativeKg")} className="trend-line-conservative" />
          <path d={linePath("assertiveKg")} className="trend-line-assertive" />
          <circle cx={xForWeek(end.week)} cy={yForKg(end.conservativeKg)} r="3.5" className="trend-point" />
          <circle cx={xForWeek(end.week)} cy={yForKg(end.assertiveKg)} r="3.5" className="trend-point" />
          <text x={padding} y={height - 6} className="trend-label">
            now
          </text>
          <text x={width - padding} y={height - 6} textAnchor="end" className="trend-label">
            {end.week} wk
          </text>
          <text x={padding} y={16} className="trend-label">
            {displayValue(maxValue)}
          </text>
          <text x={padding} y={height - padding - 6} className="trend-label">
            {displayValue(minValue)}
          </text>
        </svg>
      </div>
      <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div className="rounded-md bg-white/10 px-3 py-2">
          <span className="block text-slate-400">Now</span>
          <span className="font-semibold text-white">{displayValue(start.conservativeKg)}</span>
        </div>
        <div className="rounded-md bg-white/10 px-3 py-2">
          <span className="block text-slate-400">At week {end.week}</span>
          <span className="font-semibold text-white">{expectedRange}</span>
        </div>
      </div>
      <p className="mt-3 text-xs font-semibold text-blue-200">{trend.weeklyRangeText}</p>
      <p className="mt-4 text-xs leading-5 text-slate-400">{trend.summary}</p>
    </div>
  );
}

function ResultGuidance({ recommendation }: { recommendation: { title: string; text: string; affiliateUrl?: string } }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white p-5 text-slate-900">
      <div className="flex items-start gap-3">
        <HeartPulse className="mt-0.5 text-blue-600" />
        <div>
          <h3 className="font-semibold">{recommendation.title}</h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">{recommendation.text}</p>
          {recommendation.affiliateUrl ? (
            <a
              href={recommendation.affiliateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-blue-700"
            >
              View food options <ArrowRight className="-rotate-45" />
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function AdSlot({ label }: { label: string }) {
  return <div className="ad-slot" aria-hidden="true" data-slot-label={label} />;
}

function TrustSections() {
  return (
    <section className="container py-16 lg:py-24">
      <div className="mx-auto flex max-w-4xl flex-col gap-20">
        <section aria-labelledby="precision-title">
          <div className="mb-7">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Why this helps</p>
            <h2 id="precision-title" className="mt-2 font-display text-3xl font-bold tracking-normal text-slate-950 sm:text-4xl">
              A clearer starting point for everyday feeding decisions.
            </h2>
          </div>
          <div className="bento-grid">
            <div className="bento-card bento-wide">
              <h3 className="text-xl font-semibold text-slate-950">Avoid accidental overfeeding</h3>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Bag tables often use broad weight ranges. This calculator starts with your pet's actual weight and the
                calories on your exact food, so the portion is less of a guess.
              </p>
            </div>
            <div className="bento-card bento-accent">
              <h3 className="text-xl font-semibold">No wet-food averages</h3>
              <p className="mt-3 text-base leading-7 opacity-90">
                Moisture, texture, and recipe purpose can change calories sharply. Use the exact kcal/kg, kcal/100g, or
                kcal/can instead of guessing from “wet food.”
              </p>
            </div>
            <div className="bento-card">
              <h3 className="text-xl font-semibold text-slate-950">Useful for mixed feeding</h3>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Dry breakfast and wet dinner? Split daily calories between two foods so each portion uses that food's
                own calorie density.
              </p>
            </div>
            <div className="bento-card bento-dark">
              <h3 className="text-xl font-semibold">Built from recognised guidance</h3>
              <p className="mt-3 text-base leading-7 opacity-80">
                The method follows veterinary calorie-estimation and label-reading resources from WSAVA, AAHA, AAFCO,
                FEDIAF, FDA, and Pet Nutrition Alliance.
              </p>
            </div>
          </div>
        </section>
        <ArticleBlock
          icon={FlaskConical}
          title="How We Calculate Your Pet's Needs"
          body={[
            "FoodBowlScience starts with your pet's weight and estimates daily calories using a standard veterinary energy formula. This is a starting point, not a prescription.",
            "The calculator then adjusts the estimate for dogs or cats and for broad life-stage goals such as adult maintenance, growth, obesity-prone pets, or weight loss. Body condition, activity, neuter status, illness, medication, and growth rate can all change the right amount.",
            "For dry food, wet food, and treats, the key is using the calorie value from that exact product as fed. Wet food usually has fewer calories per gram because much of its weight is water. The calculator accepts kcal/kg or kcal/100g and converts that into grams per day.",
          ]}
        />

        <ArticleBlock
          icon={BookOpen}
          title="Why Bag Recommendations Are Often Wrong"
          body={[
            "Feeding tables on pet food bags are intentionally broad. EU, UK, and US labels may present feeding amounts differently, but they all have to simplify real pets into ranges and assumptions.",
            "This calculator uses your pet's exact body weight instead of a broad weight band. A 20 kg pet is not simply fed twice as much as a 10 kg pet, because larger animals generally require fewer calories per kilogram than smaller animals.",
            "The best real-world check is trend monitoring: weigh your pet regularly, track body condition, and adjust gradually with veterinary guidance when weight is moving in the wrong direction.",
          ]}
        />

        {SHOW_AD_PLACEHOLDERS ? <AdSlot label="Google AdSense responsive in-article placement" /> : null}

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          <TrustCard title="Method Details" href={routePaths.method} text="See the formulas, assumptions, and where individual pets can differ." />
          <TrustCard title="Label Guide" href={routePaths["label-guide"]} text="Learn where to find EU/UK energy values, US calorie statements, and adequacy wording." />
          <TrustCard title="Articles" href={routePaths.articles} text="Read long-form guides on overfeeding, underfeeding, labels, and healthy weight." />
          <TrustCard title="Safety Notes" href={routePaths.safety} text="Understand when a calculator should not replace a veterinary feeding plan." />
        </div>

        <References />
        <aside>
          <div className="rounded-lg border bg-slate-50 p-7">
            <h2 className="text-lg font-semibold text-slate-950">How to use the result safely</h2>
            <ul className="mt-5 grid gap-3 text-base leading-7 text-slate-600 sm:grid-cols-2">
              <li>Use the calories printed on the exact food, not an average for dry or wet food.</li>
              <li>Count treats, toppers, chews, and table food in the daily calorie total.</li>
              <li>Weigh your pet regularly and adjust portions gradually if weight is moving the wrong way.</li>
              <li>Ask a veterinarian before weight loss, pregnancy, lactation, illness, or prescription diets.</li>
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}

function ArticleBlock({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string[] }) {
  return (
    <article className="grid gap-6 md:grid-cols-[64px_1fr]">
      <div className="flex size-14 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
        <Icon aria-hidden="true" />
      </div>
      <div>
        <h2 className="text-3xl font-semibold leading-tight tracking-normal text-slate-950 sm:text-4xl">{title}</h2>
        <div className="article-prose mt-6">
          {body.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </div>
    </article>
  );
}

function TrustCard({ title, text, href }: { title: string; text: string; href: string }) {
  return (
    <a href={href} className="group rounded-lg border bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <h3 className="flex items-center justify-between gap-3 text-lg font-semibold text-slate-950">
        {title}
        <ArrowRight className="text-blue-600 transition group-hover:translate-x-1" />
      </h3>
      <p className="mt-3 text-sm leading-6 text-slate-600">{text}</p>
    </a>
  );
}

function ArticlesHub() {
  return (
    <main className="container py-12 lg:py-20">
      <a href={routePaths.home} className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">
        <ArrowRight className="rotate-180" />
        Back to calculator
      </a>
      <div className="max-w-4xl">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Articles library</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-normal text-slate-950 sm:text-5xl">Useful feeding guidance for real-world pet owners.</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">
          These articles turn published research, veterinary guidelines, and regulatory nutrition references into plain
          language. They are written to help ordinary owners make calmer, better feeding decisions without pretending a
          website can replace a veterinarian.
        </p>
      </div>

      <div className="mt-12 grid max-w-5xl gap-8 lg:grid-cols-2">
        {articlesNewestFirst.map((article) => (
          <article key={article.slug} className="overflow-hidden rounded-lg border bg-white shadow-sm">
            {article.image ? (
              <div className="border-b bg-slate-100">
                <img src={article.image.src} alt={article.image.alt} className="aspect-[16/9] w-full object-cover" loading="lazy" decoding="async" />
              </div>
            ) : null}
            <div className="p-7">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-2xl font-semibold tracking-normal text-slate-950">{article.title}</h2>
                <p className="mt-2 text-sm text-slate-500">
                  <time dateTime={article.published}>{formatPublished(article.published)}</time>
                </p>
                <p className="mt-3 text-base leading-7 text-slate-600">{article.summary}</p>
              </div>
              <Badge>{article.evidence}</Badge>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={articlePath(article.slug)}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                Read article
                <ArrowRight data-icon="inline-end" />
              </a>
              <a href="/#calculator" className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                Use the calculator
              </a>
            </div>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}

function ArticlePage({ article }: { article: Article }) {
  const relatedArticles = article.relatedSlugs.map((slug) => articlesBySlug[slug]).filter(Boolean);

  return (
    <main className="container py-12 lg:py-20">
      <a href={routePaths.articles} className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">
        <ArrowRight className="rotate-180" />
        Back to articles
      </a>
      <article className="mx-auto max-w-4xl rounded-2xl border bg-white p-6 shadow-sm sm:p-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Articles</p>
            <h1 className="mt-3 text-4xl font-semibold tracking-normal text-slate-950 sm:text-5xl">{article.title}</h1>
            <p className="mt-3 text-sm text-slate-500">
              Published <time dateTime={article.published}>{formatPublished(article.published)}</time>
            </p>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-600">{article.summary}</p>
          </div>
          <Badge>{article.evidence}</Badge>
        </div>

        <div className="mt-6 rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-600">
          <p className="font-semibold text-slate-900">{article.reviewNote}</p>
          <p className="mt-2">Source links open in a new window.</p>
        </div>

        {article.image ? (
          <div className="mt-8 overflow-hidden rounded-2xl border">
            <img src={article.image.src} alt={article.image.alt} className="aspect-[16/9] w-full object-cover" loading="lazy" decoding="async" />
          </div>
        ) : null}

        <div className="article-prose mt-8">
          {article.bodySections.map((section) => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.bullets?.length ? (
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              ) : null}
              {section.callout ? (
                <div
                  className={cn(
                    "mb-8 rounded-xl border px-5 py-4",
                    section.callout.tone === "warning" ? "border-amber-300 bg-amber-50" : "border-blue-200 bg-blue-50",
                  )}
                >
                  <h3 className="mb-2 mt-0 text-lg font-semibold text-slate-950">{section.callout.title}</h3>
                  {section.callout.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="mb-0 text-base leading-7 text-slate-700">
                      {paragraph}
                    </p>
                  ))}
                </div>
              ) : null}
            </section>
          ))}
        </div>

        <div className="mt-10 grid gap-6 border-t pt-8 lg:grid-cols-[1.25fr_0.75fr]">
          <section>
            <h2 className="text-2xl font-semibold tracking-normal text-slate-950">Sources used</h2>
            <div className="mt-4 grid gap-3">
              {article.sources.map((source) => (
                <a
                  key={source.url}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg bg-slate-50 px-4 py-4 transition hover:bg-blue-50"
                >
                  <span className="flex items-start justify-between gap-4 text-sm font-semibold text-blue-700">
                    <span>{source.label}</span>
                    <ArrowRight className="-rotate-45 shrink-0" aria-hidden="true" />
                  </span>
                  {source.note ? <span className="mt-2 block text-sm leading-6 text-slate-600">{source.note}</span> : null}
                </a>
              ))}
            </div>
          </section>
          <aside className="space-y-4">
            <div className="rounded-xl border bg-slate-50 p-5">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-500">Next step</p>
              <h3 className="mt-2 text-xl font-semibold text-slate-950">Use the calculator with your exact food label.</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                These articles explain the why. The calculator helps you turn the actual kcal number on your food into a
                daily starting portion.
              </p>
              <a
                href="/#calculator"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                Open calculator
                <Calculator data-icon="inline-end" />
              </a>
            </div>
            <div className="rounded-xl border p-5">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-500">Related reading</p>
              <div className="mt-4 grid gap-3">
                {relatedArticles.map((relatedArticle) => (
                  <a key={relatedArticle.slug} href={articlePath(relatedArticle.slug)} className="rounded-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-950">
                    {relatedArticle.title}
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>
    </main>
  );
}

function DetailPage({ page }: { page: "method" | "label-guide" | "safety" | "privacy" }) {
  const content: { title: string; intro: string; sections: { title: string; text: React.ReactNode }[] } = {
    ...staticPages,
    privacy: {
      title: "Privacy",
      intro:
        "This notice describes FoodBowlScience as of May 2026. It is short because the calculator is designed to avoid collecting pet profiles or account data.",
      sections: [
        {
          title: "Operator",
          text: "FoodBowlScience. For any questions or comments, please contact foodbowlscience.0h8zm@simplelogin.com.",
        },
        {
          title: "What the calculator sends",
          text:
            "The calculator runs in your browser. Pet type, weight, food calories, meal settings, and calculator results are not sent to a FoodBowlScience server and are not stored in an account.",
        },
        {
          title: "Local preference storage",
          text:
            "The site stores one item in your browser's localStorage: your light or dark mode preference, saved as foodbowlscience-theme. It is not used for tracking and can be removed through your browser settings.",
        },
        {
          title: "Analytics",
          text:
            "The site uses Cloudflare Web Analytics to count page views and see which pages get used. It is privacy-friendly: it sets no cookies, collects no personal data, and does not build a profile of you or follow you across other sites.",
        },
        {
          title: "Advertising",
          text: (
            <>
              This site may display ads served by Google AdSense. Google and its partners may use cookies to serve ads
              based on your prior visits to this site and other sites. You can opt out of personalised advertising at{" "}
              <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700">
                Google's Ads Settings
              </a>
              . You can also opt out via{" "}
              <a href="https://www.aboutads.info" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700">
                aboutads.info
              </a>
              . Where required by law, a consent prompt will appear before personalised ads are shown.
            </>
          ),
        },
        {
          title: "Hosting logs",
          text: (
            <>
              The site is hosted by Cloudflare{" "}
              <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700">
                cloudflare.com/privacypolicy
              </a>
              . Like most hosting providers, Cloudflare may process standard technical data, including IP address,
              browser type, requested page, and timestamp, to deliver the site, troubleshoot errors, and protect
              against abuse. This processing is based on legitimate interests in operating a secure and functional
              website. FoodBowlScience does not use those logs to build pet feeding profiles.
            </>
          ),
        },
        {
          title: "Your rights",
          text:
            "Where data protection law applies, you have the right to request access to, erasure of, or restriction of your personal data; to object to its processing; and to lodge a complaint with a supervisory authority. Because this site does not store calculator inputs, any personal data held is limited to technical hosting logs. Requests can be sent to the contact address above.",
        },
      ],
    },
  }[page];

  return (
    <main className="container py-12 lg:py-20">
      <a href={routePaths.home} className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-blue-700">
        <ArrowRight className="rotate-180" />
        Back to calculator
      </a>
      <div className="max-w-4xl">
        <h1 className="text-4xl font-semibold tracking-normal text-slate-950 sm:text-5xl">{content.title}</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">{content.intro}</p>
      </div>
      <div className="mt-12 grid max-w-4xl gap-6">
        {content.sections.map((section) => (
          <Card key={section.title}>
            <CardHeader>
              <CardTitle className="text-xl">{section.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-base leading-8 text-slate-600">{section.text}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      {relatedForPage[page]?.length ? (
        <div className="mt-14 max-w-4xl">
          <h2 className="text-xl font-semibold text-slate-950">Related reading</h2>
          <div className="mt-4 grid gap-3">
            {relatedForPage[page].map((slug) => {
              const a = articlesBySlug[slug];
              return a ? (
                <a key={slug} href={articlePath(slug)} className="rounded-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-950">
                  {a.title}
                </a>
              ) : null;
            })}
          </div>
        </div>
      ) : null}
      {page === "method" || page === "label-guide" ? <CalculatorPageLinks /> : null}
      {page === "method" || page === "label-guide" ? (
        <div className="mt-16 max-w-4xl">
          <References />
        </div>
      ) : null}
    </main>
  );
}

function CalculatorPageLinks() {
  return (
    <div className="mt-14 max-w-4xl">
      <h2 className="text-xl font-semibold text-slate-950">Calculators</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {calculatorPageKeys.map((key) => (
          <a key={key} href={routePaths[key]} className="rounded-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-950">
            {calculatorPages[key].h1}
          </a>
        ))}
      </div>
    </div>
  );
}

function CalculatorLandingPage({ pageKey }: { pageKey: CalculatorPageRoute }) {
  const p = calculatorPages[pageKey];
  return (
    <main className="container py-12 lg:py-20">
      <div className="max-w-4xl">
        <h1 className="text-4xl font-semibold tracking-normal text-slate-950 sm:text-5xl">{p.h1}</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">{p.intro}</p>
      </div>
      <div className="mt-10">
        <CalculatorPanel key={pageKey} initialSpecies={p.species} />
      </div>
      <div className="mt-16 grid max-w-4xl gap-6">
        {p.sections.map((section) => (
          <Card key={section.title}>
            <CardHeader>
              <h2 className="text-xl font-semibold leading-none tracking-normal">{section.title}</h2>
            </CardHeader>
            <CardContent className="space-y-4">
              {section.paragraphs.map((text) => (
                <p key={text} className="text-base leading-8 text-slate-600">
                  {text}
                </p>
              ))}
              {section.bullets?.length ? (
                <ul className="list-disc space-y-1 pl-6 text-base leading-7 text-slate-600">
                  {section.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="mt-14 max-w-4xl">
        <h2 className="text-xl font-semibold text-slate-950">Common questions</h2>
        <div className="mt-4 grid gap-6">
          {p.faqs.map((faq) => (
            <Card key={faq.q}>
              <CardHeader>
                <h3 className="text-lg font-semibold leading-7 tracking-normal">{faq.q}</h3>
              </CardHeader>
              <CardContent>
                <p className="text-base leading-8 text-slate-600">{faq.a}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      <div className="mt-14 max-w-4xl">
        <h2 className="text-xl font-semibold text-slate-950">Sources used</h2>
        <ul className="mt-4 grid gap-2 text-sm leading-6">
          {p.sources.map((s) => (
            <li key={s.url}>
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700 underline underline-offset-4">
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-14 max-w-4xl">
        <h2 className="text-xl font-semibold text-slate-950">More on feeding</h2>
        <div className="mt-4 grid gap-3">
          <a href={routePaths["label-guide"]} className="rounded-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-950">
            How to read a pet food label
          </a>
          <a href={routePaths.method} className="rounded-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-950">
            How the calculator works
          </a>
          {p.related.map((slug) => {
            const a = articlesBySlug[slug];
            return a ? (
              <a key={slug} href={articlePath(slug)} className="rounded-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-950">
                {a.title}
              </a>
            ) : null;
          })}
        </div>
      </div>
    </main>
  );
}

function References() {
  return (
    <section aria-labelledby="references-title">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Badge>Pet-owner references</Badge>
        <h2 id="references-title" className="text-2xl font-semibold tracking-normal text-slate-950">
          Sources used
        </h2>
      </div>
      <div className="grid gap-4">
        {references.map((reference) => (
          <a
            key={reference.url}
            href={reference.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg border bg-white p-5 shadow-sm transition hover:border-blue-200 hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-base font-semibold text-slate-950">{reference.title}</div>
                <div className="mt-1 text-xs font-medium uppercase text-slate-500">{reference.source}</div>
                <p className="mt-3 text-base leading-7 text-slate-600">{reference.note}</p>
              </div>
              <ArrowRight className="-rotate-45 shrink-0 text-blue-600" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function DisclaimerFooter({ onClose }: { onClose: () => void }) {
  return (
    <footer className="fixed inset-x-4 bottom-4 z-40 rounded-lg border bg-white/95 px-4 py-2 shadow-clinical backdrop-blur md:bottom-auto md:left-auto md:right-6 md:top-20 md:w-[430px]">
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 text-xs leading-5 text-slate-600">
        <span className="truncate">
          Veterinary disclaimer: estimates only; consult your veterinarian for medical feeding plans.
        </span>
        <a href={routePaths.safety} className="inline-flex shrink-0 items-center gap-1 font-semibold text-blue-700" aria-label="Safety notes">
          Safety <ArrowRight />
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close veterinary disclaimer"
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
        >
          <X aria-hidden="true" />
        </button>
      </div>
    </footer>
  );
}

export default App;
